import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import { HiOutlineArrowLeft } from "react-icons/hi2";

import { authService, masterDataService, hazardReportService } from "../services";
import userService from "../services/userService";
import { toast } from "react-toastify";
import "./HazardSettings.css";

const HazardSettings = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    facilityLevel: "",
    enableSeverity: true,
    enableImmediateAction: true,
    enableImmediateDangerTracking: true,
    requireEvidence: false,
    enableEmailNotifications: true,
    notifyOnSubmission: "",
    notifyOnEscalation: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await hazardReportService.getHazardSettings();
        if (data) {
          setFormData({
            facilityLevel:
              (data.facilityLevel ?? data.FacilityLevel)
                ? String(data.facilityLevel ?? data.FacilityLevel)
                : "",
            enableSeverity: data.enableSeverity ?? data.EnableSeverity ?? true,
            enableImmediateAction:
              data.enableImmediateAction ?? data.EnableImmediateAction ?? true,
            enableImmediateDangerTracking:
              data.enableImmediateDangerTracking ??
              data.EnableImmediateDangerTracking ??
              true,
            requireEvidence:
              data.requireEvidence ?? data.RequireEvidence ?? false,
            enableEmailNotifications:
              data.enableEmailNotifications ??
              data.EnableEmailNotifications ??
              true,
            notifyOnSubmission:
              data.notifyOnSubmission ?? data.NotifyOnSubmission ?? "",
            notifyOnEscalation:
              data.notifyOnEscalation ?? data.NotifyOnEscalation ?? "",
          });
        }
      } catch (error) {
        console.error("Error loading hazard settings:", error);
        // Non-blocking error for initial load
      }

      try {
        const tenantId = authService.getTenantId();
        const [usersData, rolesData] = await Promise.all([
          userService.getAllUsers(),
          masterDataService.getRoles(tenantId)
        ]);
        if (usersData && Array.isArray(usersData)) {
          setUsers(
            usersData.filter(
              (u) =>
                u.status === "Active" ||
                u.Status === "Active" ||
                (!u.status && !u.Status),
            ),
          );
        }
        setRoles(rolesData || []);
      } catch (err) {
        console.error("Error loading extra data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        enableEmailNotifications: true,
        facilityLevel: formData.facilityLevel
          ? parseInt(formData.facilityLevel, 10)
          : null,
      };
      await hazardReportService.saveHazardSettings(payload);
      toast.success("Hazard configuration saved successfully!");
    } catch (err) {
      console.error("Error saving hazard settings:", err);
      toast.error("Failed to save configuration settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hazard-settings-container">
        <div className="settings-loading">
          <div className="spinner"></div>
          <p>Loading hazard module configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hazard-settings-container">
      <div className="settings-header">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          title="Go Back"
        >
          <HiOutlineArrowLeft />
        </button>
        <h2>Hazard Module Configuration</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="hazard-settings-section">
          <div className="settings-header">
            <h3 style={{ margin: 0, fontSize: "1.2rem", color: "#1e293b" }}>
              General Settings
            </h3>
          </div>

          <div className="hazard-settings-form">
            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Max Facility level for hazard reporting"
                />
                <label>
                  Up to what Facility level you want to perform hazard reporting
                </label>
              </div>
              <div className="input-col">
                <select
                  name="facilityLevel"
                  value={formData.facilityLevel}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="">Select Level</option>
                  {[1, 2, 3, 4, 5, 6].map((lvl) => (
                    <option key={lvl} value={String(lvl)}>
                      Level {lvl}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Enable capturing Initial Severity Estimate"
                />
                <label>
                  Do you want to evaluate Initial Severity of the hazard?
                </label>
              </div>
              <div className="input-col">
                <label className="switch">
                  <input
                    type="checkbox"
                    name="enableSeverity"
                    checked={formData.enableSeverity}
                    onChange={handleInputChange}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Enable Immediate Action tracking"
                />
                <label>
                  Require detailing Immediate Action Taken during reporting?
                </label>
              </div>
              <div className="input-col">
                <label className="switch">
                  <input
                    type="checkbox"
                    name="enableImmediateAction"
                    checked={formData.enableImmediateAction}
                    onChange={handleInputChange}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Enable Immediate Danger / Urgency indicator"
                />
                <label>
                  Allow reporter to mark hazards as "Immediate Danger"?
                </label>
              </div>
              <div className="input-col">
                <label className="switch">
                  <input
                    type="checkbox"
                    name="enableImmediateDangerTracking"
                    checked={formData.enableImmediateDangerTracking}
                    onChange={handleInputChange}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Require photographic evidence or attachments"
                />
                <label>
                  Is photographic evidence required to submit a hazard report?
                </label>
              </div>
              <div className="input-col">
                <label className="switch">
                  <input
                    type="checkbox"
                    name="requireEvidence"
                    checked={formData.requireEvidence}
                    onChange={handleInputChange}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="hazard-settings-section">
          <div className="settings-header">
            <h3 style={{ margin: 0, fontSize: "1.2rem", color: "#1e293b" }}>
              Notification Settings
            </h3>
          </div>

          <div className="hazard-settings-form">
            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Notify on report submission"
                />
                <label>
                  Who should be notified when hazard is reported via email &
                  Notification
                </label>
              </div>
              <div className="input-col">
                <select
                  name="notifyOnSubmission"
                  value={formData.notifyOnSubmission}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="">Select recipient</option>
                  <optgroup label="Roles">
                    {roles.map((r) => (
                      <option key={r.roleId || r.id} value={r.roleName || r.name}>
                        {r.roleName || r.name}
                      </option>
                    ))}
                  </optgroup>

                </select>
              </div>
            </div>

            <div className="form-group-row">
              <div className="label-col">
                <FiInfo
                  className="info-icon"
                  title="Notify on escalation to CAPA"
                />
                <label>
                  Who should be notified when hazard is escalated to CAPA via
                  email & Notification
                </label>
              </div>
              <div className="input-col">
                <select
                  name="notifyOnEscalation"
                  value={formData.notifyOnEscalation}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="">Select recipient</option>
                  <optgroup label="Roles">
                    {roles.map((r) => (
                      <option key={r.roleId || r.id} value={r.roleName || r.name}>
                        {r.roleName || r.name}
                      </option>
                    ))}
                  </optgroup>

                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions-row">
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? "Saving Changes..." : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HazardSettings;
